'use client'
import { useCallback, useMemo } from 'react'
import useSWR from 'swr/immutable'
import type { PriceResponse } from '@/root/shared-types/dto/SailPricing'
import type { SailRefParam } from '@/root/shared-types/selectTicketPage/selectTicket'
import { getSailInventorySummary } from '@/root/modules/shared/SelectTicketPage/helpers/getSailInventorySummary/getSailInventorySummary'
import { useCustomRouter, useGetInventory } from '@/root/shared-hooks'
import { APP_ROUTES } from '@/root/shared-consts'

/** Prices and remaining capacity of the chosen sails (one, or two on a round trip). */
export const useTicketPrices = (sailParams: SailRefParam[]) => {
  const { locale } = useCustomRouter()
  const [sailOne, sailTwo] = sailParams

  const firstCode = sailOne?.code
  const firstSailRefId = sailOne?.sailRefIds[0]
  const secondCode = sailTwo?.code
  const secondSailRefId = sailTwo?.sailRefIds[0]

  const { data: prices, error, isLoading } = useSWR<PriceResponse>(`/api/prices/${firstSailRefId}`)
  const { data: pricesBack } = useSWR<PriceResponse>(secondSailRefId ? `/api/prices/${secondSailRefId}` : null)

  const firstDate = prices?.sailRef.departureDate
  const secondDate = pricesBack?.sailRef.departureDate

  const packages = [firstCode, secondCode].filter(Boolean).join(',')
  const dates = [firstDate, secondDate].filter(Boolean).join(',')

  const { inventory: inventoryThere, mutate: mutateInventoryThere } = useGetInventory({
    locale,
    sailPackage: firstCode,
    date: firstDate || '',
  })
  const {
    inventory: inventoryBack,
    isLoading: isLoadingBack,
    mutate: mutateInventoryBack,
  } = useGetInventory({
    locale,
    sailPackage: secondCode ?? '',
    date: secondDate ?? '',
  })

  const capacityThere = useMemo(
    () => getSailInventorySummary({ sailRefId: firstSailRefId, inventory: inventoryThere }),
    [firstSailRefId, inventoryThere]
  )
  const capacityBack = useMemo(
    () => getSailInventorySummary({ sailRefId: secondSailRefId, inventory: inventoryBack }),
    [secondSailRefId, inventoryBack]
  )

  const revalidateInventory = useCallback(() => {
    mutateInventoryThere()
    if (secondSailRefId) mutateInventoryBack()
  }, [mutateInventoryThere, mutateInventoryBack, secondSailRefId])

  return {
    prices,
    pricesBack,
    error,
    isLoading,
    capacityThere,
    // Until the return sail's inventory loads, the outbound sail alone limits a round trip
    capacityBack: secondSailRefId && !isLoadingBack ? capacityBack : undefined,
    revalidateInventory,
    backHref: `/${locale}${APP_ROUTES.selectDateSail}/${packages}/${dates}`,
  }
}
